import PropTypes from 'prop-types';
import classNames from 'classnames';

// Higher Order Components
import Dropzone from 'react-dropzone';

import { ACCEPTED_IMAGE_TYPES, MIME_TO_EXTENSIONS, FILE_SIZE_LIMITS } from '../../constants/file-upload.js';
import s from './ImageDropzone.module.scss';

/**
 * Converts acceptedFileTypes array to react-dropzone v14 accept object format
 * @param {string[]} fileTypes - Array of MIME types like ['image/jpeg', 'image/png']
 * @returns {Object} Accept object in format { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] }
 */
// eslint-disable-next-line react-refresh/only-export-components -- Exported for unit tests in test/convert-accept-format.test.js.
export const convertAcceptFormat = (fileTypes) => {
    const inputMimeTypes = Array.isArray(fileTypes) ? fileTypes : [];

    return inputMimeTypes.reduce((acceptByMimeType, mimeType) => {
        const fileExtensionsForMimeType = MIME_TO_EXTENSIONS[mimeType];

        if (fileExtensionsForMimeType) {
            acceptByMimeType[mimeType] = fileExtensionsForMimeType;
        }

        return acceptByMimeType;
    }, {});
};

const ImageDropzone = ({ onFileDrop, multiple, acceptedFileTypes, maxFileSize, disabled }) => {
    const onDrop = (accepted, fileRejections) => {
        const rejected = fileRejections.map(fr => fr.file);
        onFileDrop({ accepted, rejected });
    };

    // Convert array format to object format for react-dropzone v14
    const acceptObject = convertAcceptFormat(acceptedFileTypes);

    return (
        <Dropzone
            disabled={disabled}
            accept={acceptObject}
            onDrop={onDrop}
            maxSize={maxFileSize}
            multiple={multiple}
        >
            {({ getRootProps, getInputProps }) => (
                <div className={classNames(s.uploader, s.imgChildContainer)} {...getRootProps()} data-testid={'image-dropzone'}>
                    <input {...getInputProps()} />
                    <div className={s.imgContainer}>
                        <div className={s.circleBg}>
                            <img
                                className={classNames(s.uploadImgHoverFan, s.leftFan)}
                                src="https://content.locuslabs.com/vms-ui/placeholder-icons/image-placeholder-icon-side.svg"
                                alt="Uploader Hover Left"
                            />
                            <img
                                className={classNames(s.uploadImgHoverFan, s.rightFan)}
                                src="https://content.locuslabs.com/vms-ui/placeholder-icons/image-placeholder-icon-side.svg"
                                alt="Uploader Hover Right"
                            />
                            <img className={s.uploadImg} src="https://content.locuslabs.com/vms-ui/placeholder-icons/image-placeholder-icon.svg" alt="Uploader" />
                            <img className={s.uploadImgHover} src="https://content.locuslabs.com/vms-ui/placeholder-icons/image-placeholder-icon-blue.svg" alt="Uploader Hover" />
                        </div>
                    </div>
                    <div className={s.uploadPrompt}>
                        Drag or
                        {<span className={s.browseClick}>browse</span>}
                        to upload
                    </div>
                </div>
            )}
        </Dropzone>
    );
};

ImageDropzone.propTypes = {
    onFileDrop: PropTypes.func.isRequired,
    multiple: PropTypes.bool,
    acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
    maxFileSize: PropTypes.number,
    disabled: PropTypes.bool,
};

ImageDropzone.defaultProps = {
    multiple: false,
    acceptedFileTypes: ACCEPTED_IMAGE_TYPES,
    maxFileSize: FILE_SIZE_LIMITS.DEFAULT,
    disabled: false,
};

export default ImageDropzone;
